// src/component/VillageMapLeaflet.js
import React, { useState, useRef, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// カテゴリ定義
const MAP_CATEGORIES = [
  { key: "all", label: "すべて", color: "#4070e0", icon: "" },
  { key: "shop", label: "村民さんのお店", color: "#e74c3c", icon: "🏪" },
  { key: "base", label: "拠点", color: "#2ecc71", icon: "📍" },
  { key: "farmer", label: "お米農家", color: "#f39c12", icon: "🌾" },
  { key: "rice", label: "お米販売", color: "#9b59b6", icon: "🍚" },
];

// 画像（カテゴリ別）
const SHOP_IMAGES = [
  "https://images.unsplash.com/photo-1559925393-8be0ec4767c8?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1611085583191-a3b181a88401?w=400&h=250&fit=crop",
];
const BASE_IMAGES = [
  "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1462826303086-329426d1afd5?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1527192491265-7e15c55b1ed2?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1497215842964-222b430dc094?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=400&h=250&fit=crop",
];
const FARMER_IMAGES = [
  "https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1523741543316-beb7fc7023d8?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1595841696677-6589b7386750?w=400&h=250&fit=crop",
];
const RICE_IMAGES = [
  "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1536304929831-ee1ca9d44571?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1506617420156-8e4536971650?w=400&h=250&fit=crop",
];

function getImage(marker) {
  const idx = marker.id;
  if (marker.category === "shop") return SHOP_IMAGES[idx % SHOP_IMAGES.length];
  if (marker.category === "base") return BASE_IMAGES[idx % BASE_IMAGES.length];
  if (marker.category === "farmer") return FARMER_IMAGES[idx % FARMER_IMAGES.length];
  if (marker.category === "rice") return RICE_IMAGES[idx % RICE_IMAGES.length];
  return SHOP_IMAGES[0];
}

// マーカーデータ
const MARKERS = [
  // 村民さんのお店
  { id: 1, name: "自然食レストラン 大地", category: "shop", lat: 35.6895, lng: 139.6917, address: "東京都新宿区西新宿1-1-1", description: "オーガニック食材を使った自然食レストラン", phone: "03-1234-5678" },
  { id: 2, name: "手作り雑貨 ひだまり", category: "shop", lat: 34.6937, lng: 135.5023, address: "大阪府大阪市北区梅田1-2-3", description: "ハンドメイドの雑貨・アクセサリー店", phone: "06-1234-5678" },
  { id: 3, name: "整体院 和み", category: "shop", lat: 35.1815, lng: 136.9066, address: "愛知県名古屋市中区栄3-4-5", description: "心と体を整える整体院", phone: "052-123-4567" },
  { id: 4, name: "ヨガスタジオ LUNA", category: "shop", lat: 33.5904, lng: 130.4017, address: "福岡県福岡市中央区天神2-3-4", description: "少人数制のヨガスタジオ", phone: "092-123-4567" },
  { id: 5, name: "オーガニックカフェ 森のテラス", category: "shop", lat: 43.0621, lng: 141.3544, address: "北海道札幌市中央区南1条西5", description: "自家焙煎コーヒーとオーガニックスイーツ", phone: "011-123-4567" },
  { id: 6, name: "天然石アクセサリー 月虹", category: "shop", lat: 35.0116, lng: 135.7681, address: "京都府京都市中京区河原町通", description: "天然石を使ったオリジナルアクセサリー", phone: "075-123-4567" },
  // 拠点
  { id: 10, name: "東京拠点", category: "base", lat: 35.6762, lng: 139.6503, address: "東京都渋谷区神宮前5-6-7", description: "関東エリアの活動拠点。毎月の新月会・満月会はここから配信。", phone: "" },
  { id: 11, name: "大阪拠点", category: "base", lat: 34.7025, lng: 135.4959, address: "大阪府大阪市中央区難波1-2-3", description: "関西エリアの活動拠点。月2回の交流会を開催。", phone: "" },
  { id: 12, name: "名古屋拠点", category: "base", lat: 35.1709, lng: 136.8815, address: "愛知県名古屋市中村区名駅3-4-5", description: "中部エリアの活動拠点。", phone: "" },
  { id: 13, name: "福岡拠点", category: "base", lat: 33.5902, lng: 130.4017, address: "福岡県福岡市博多区博多駅前2-3-4", description: "九州エリアの活動拠点。", phone: "" },
  { id: 14, name: "札幌拠点", category: "base", lat: 43.0687, lng: 141.3508, address: "北海道札幌市北区北7条西4", description: "北海道エリアの活動拠点。", phone: "" },
  { id: 15, name: "仙台拠点", category: "base", lat: 38.2682, lng: 140.8694, address: "宮城県仙台市青葉区中央1-2-3", description: "東北エリアの活動拠点。", phone: "" },
  { id: 16, name: "広島拠点", category: "base", lat: 34.3853, lng: 132.4553, address: "広島県広島市中区紙屋町1-2-3", description: "中国・四国エリアの活動拠点。", phone: "" },
  { id: 17, name: "那覇拠点", category: "base", lat: 26.3344, lng: 127.8056, address: "沖縄県那覇市久茂地3-1-1", description: "沖縄エリアの活動拠点。", phone: "" },
  // お米農家
  { id: 20, name: "田中農園", category: "farmer", lat: 36.5613, lng: 139.8836, address: "栃木県宇都宮市田野町", description: "無農薬コシヒカリを栽培。30年以上の有機農法の経験。", phone: "028-123-4567" },
  { id: 21, name: "山本自然農園", category: "farmer", lat: 37.9024, lng: 139.0231, address: "新潟県南魚沼市塩沢", description: "魚沼産コシヒカリの自然栽培農家。天日干しにこだわり。", phone: "025-123-4567" },
  { id: 22, name: "佐藤ファーム", category: "farmer", lat: 39.7186, lng: 140.1024, address: "秋田県大仙市大曲", description: "あきたこまちの有機栽培。合鴨農法を実践。", phone: "018-123-4567" },
  { id: 23, name: "井上稲作", category: "farmer", lat: 33.2396, lng: 131.6127, address: "大分県竹田市荻町", description: "棚田で育てる自然栽培米。ヒノヒカリが人気。", phone: "097-123-4567" },
  { id: 24, name: "森の田んぼ 高橋農場", category: "farmer", lat: 35.3606, lng: 136.7222, address: "滋賀県近江八幡市安土町", description: "琵琶湖の水で育てる近江米。環境保全型農業。", phone: "074-123-4567" },
  // お米販売
  { id: 30, name: "お米のマルシェ 東京店", category: "rice", lat: 35.6580, lng: 139.7016, address: "東京都渋谷区恵比寿南1-2-3", description: "セカイムラ農家さんのお米を直売。試食コーナーあり。", phone: "03-9876-5432" },
  { id: 31, name: "お米のマルシェ 大阪店", category: "rice", lat: 34.6723, lng: 135.4980, address: "大阪府大阪市天王寺区上本町6-7-8", description: "関西で唯一の直売所。月1回の即売会も開催。", phone: "06-9876-5432" },
  { id: 32, name: "自然食品店 いのち", category: "rice", lat: 35.6585, lng: 139.7454, address: "東京都港区芝公園4-5-6", description: "セカイムラ米の取り扱い店。その他自然食品も。", phone: "03-5555-1234" },
  { id: 33, name: "道の駅 里山の恵み", category: "rice", lat: 36.3905, lng: 139.0609, address: "群馬県前橋市富士見町赤城山", description: "地元農家の新鮮野菜とセカイムラ米を販売。", phone: "027-123-4567" },
  { id: 34, name: "ネット販売 セカイムラ米ストア", category: "rice", lat: 35.6812, lng: 139.7671, address: "オンライン（東京都千代田区より配送）", description: "全国配送対応のオンラインストア。定期便あり。", phone: "" },
];

const MAP_CENTER = [35.0, 135.0];
const DEFAULT_ZOOM = 5;

// Leaflet用カスタムアイコン生成
function createIcon(category) {
  const cat = MAP_CATEGORIES.find((c) => c.key === category);
  if (!cat) return new L.Icon.Default();

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="43" viewBox="0 0 40 48">
      <path d="M20 0C9 0 0 9 0 20c0 15 20 28 20 28s20-13 20-28C40 9 31 0 20 0z" fill="${cat.color}"/>
      <circle cx="20" cy="18" r="10" fill="white"/>
      <text x="20" y="23" text-anchor="middle" font-size="16">${cat.icon}</text>
    </svg>
  `;

  return L.divIcon({
    html: svg,
    className: "",
    iconSize: [36, 43],
    iconAnchor: [18, 43],
    popupAnchor: [0, -43],
  });
}

// タイルテーマ定義（6種類）
const TILE_THEMES = [
  {
    key: "standard",
    label: "スタンダード",
    preview: "#d4e6b5",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
  {
    key: "light",
    label: "ライト",
    preview: "#e0e0e0",
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
  },
  {
    key: "dark",
    label: "ダーク",
    preview: "#263238",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
  },
  {
    key: "voyager",
    label: "ボイジャー",
    preview: "#f0efe9",
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
  },
  {
    key: "topo",
    label: "地形図",
    preview: "#d4e8c2",
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
  },
  {
    key: "watercolor",
    label: "水彩画",
    preview: "#b3d9f2",
    url: "https://tiles.stadiamaps.com/tiles/stamen_watercolor/{z}/{x}/{y}.jpg",
    attribution: '&copy; <a href="https://stamen.com">Stamen Design</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
];

// フッターナビ
const FOOTER_NAV = [
  { label: "ホーム", path: "/portal", underline: false },
  { label: "地球の活動", path: "/portal", underline: false },
  { label: "太陽の活動", path: "/portal", underline: false },
  { label: "月の活動", path: "/portal", underline: false },
  { label: "イベント", path: "/events", underline: false },
];

// マップの中心・ズームを変更するヘルパーコンポーネント
function ChangeView({ center, zoom }) {
  const map = useMap();
  map.setView(center, zoom);
  return null;
}

export default function VillageMapLeaflet() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [flyTo, setFlyTo] = useState(null);
  const [tileTheme, setTileTheme] = useState("standard");
  const mapRef = useRef(null);

  const activeTile = TILE_THEMES.find((t) => t.key === tileTheme) || TILE_THEMES[0];

  const filtered =
    activeCategory === "all"
      ? MARKERS
      : MARKERS.filter((m) => m.category === activeCategory);

  const icons = useMemo(() => {
    const map = {};
    MAP_CATEGORIES.forEach((cat) => {
      if (cat.key !== "all") {
        map[cat.key] = createIcon(cat.key);
      }
    });
    return map;
  }, []);

  const resetView = () => {
    setFlyTo({ center: MAP_CENTER, zoom: DEFAULT_ZOOM });
    setTimeout(() => setFlyTo(null), 100);
  };

  const focusMarker = (marker) => {
    setFlyTo({ center: [marker.lat, marker.lng], zoom: 13 });
    setTimeout(() => setFlyTo(null), 100);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: { xs: 2, sm: 4 },
        px: 2,
        pb: 6,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 648, overflow: "hidden" }}>
        {/* コスト情報 */}
        <Box
          sx={{
            bgcolor: "#e8f5e9",
            border: "1px solid #a5d6a7",
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#2e7d32", mb: 1 }}>
            Leaflet + OpenStreetMap コスト情報
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#555", lineHeight: 1.8 }}>
            このページは <b>Leaflet + OpenStreetMap</b> を使用しています。<br />
            ・ライブラリ（Leaflet）: <b>無料</b>（BSDライセンス、商用利用OK）<br />
            ・タイル（OSM公式サーバー）: 無料だが<b>大量アクセスは利用規約違反</b><br />
            ・本番運用には商用タイルプロバイダ推奨:<br />
            &emsp;MapTiler → 10万ロード/月 無料、超過後 <b>~$25〜/月</b><br />
            &emsp;Stadia Maps → 20万ロード/月 無料、超過後 <b>~$20〜/月</b><br />
            ・25,000人 x 毎日3回 = 月225万ロード → <b>推定 月額$50〜150（約7,500〜22,500円）</b><br />
            ・Google Maps比: <b>約1/10〜1/100のコスト</b>
          </Typography>
        </Box>

        {/* タイトル */}
        <Typography
          sx={{
            fontSize: { xs: 24, sm: 32 },
            fontWeight: 800,
            textAlign: "center",
            mb: 1,
            color: "#222",
          }}
        >
          MMM マップ
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: "#888", textAlign: "center", mb: 3 }}
        >
          村民さんのお店・拠点・お米農家・お米販売の場所がわかります
        </Typography>

        {/* カテゴリフィルター */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 1,
            mb: 3,
          }}
        >
          {MAP_CATEGORIES.map((cat) => (
            <Box
              key={cat.key}
              onClick={() => {
                setActiveCategory(cat.key);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 2,
                py: 0.7,
                borderRadius: "20px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.15s ease",
                bgcolor: activeCategory === cat.key ? cat.color : "transparent",
                color: activeCategory === cat.key ? "#fff" : cat.color,
                border:
                  activeCategory === cat.key
                    ? `1px solid ${cat.color}`
                    : `1px solid ${cat.color}66`,
                "&:hover": {
                  bgcolor:
                    activeCategory === cat.key ? cat.color : `${cat.color}15`,
                },
              }}
            >
              {cat.icon && <span>{cat.icon}</span>}
              {cat.label}
            </Box>
          ))}
        </Box>

        {/* テーマ選択 */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 3 }}>
          {TILE_THEMES.map((theme) => (
            <Box
              key={theme.key}
              onClick={() => setTileTheme(theme.key)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.4,
                cursor: "pointer",
                userSelect: "none",
                opacity: tileTheme === theme.key ? 1 : 0.5,
                transition: "all 0.15s ease",
                "&:hover": { opacity: 1 },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: theme.preview,
                  border: tileTheme === theme.key ? "2.5px solid #4070e0" : "2px solid #ccc",
                  transition: "all 0.15s ease",
                }}
              />
              <Typography sx={{ fontSize: 10, color: tileTheme === theme.key ? "#4070e0" : "#999", fontWeight: 600 }}>
                {theme.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 件数 + 日本全域ボタン */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box
            onClick={resetView}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#4070e0",
              cursor: "pointer",
              border: "1px solid #4070e066",
              borderRadius: "14px",
              px: 1.5,
              py: 0.4,
              userSelect: "none",
              "&:hover": { bgcolor: "#4070e010" },
            }}
          >
            日本全域を表示
          </Box>
          <Typography sx={{ fontSize: 12, color: "#999" }}>
            {filtered.length}件の場所
          </Typography>
        </Box>

        {/* 地図 */}
        <Box
          sx={{
            width: "100%",
            height: { xs: 400, sm: 500, md: 600 },
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid #eee",
            mb: 3,
            "& .leaflet-container": {
              width: "100%",
              height: "100%",
              zIndex: 1,
            },
          }}
        >
          <MapContainer
            center={MAP_CENTER}
            zoom={DEFAULT_ZOOM}
            style={{ width: "100%", height: "100%" }}
            ref={mapRef}
            zoomControl={true}
            scrollWheelZoom={false}
          >
            <TileLayer
              key={activeTile.key}
              attribution={activeTile.attribution}
              url={activeTile.url}
            />

            {flyTo && (
              <ChangeView center={flyTo.center} zoom={flyTo.zoom} />
            )}

            {filtered.map((marker) => (
              <Marker
                key={marker.id}
                position={[marker.lat, marker.lng]}
                icon={icons[marker.category]}
              >
                <Popup maxWidth={280} minWidth={220}>
                  <div style={{ margin: 0, padding: 0 }}>
                    <img
                      src={getImage(marker)}
                      alt={marker.name}
                      style={{
                        width: "100%",
                        height: 130,
                        objectFit: "cover",
                        display: "block",
                        borderRadius: "4px 4px 0 0",
                        marginBottom: 8,
                      }}
                    />
                    <div style={{ padding: "0 4px 4px" }}>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color:
                            MAP_CATEGORIES.find(
                              (c) => c.key === marker.category
                            )?.color,
                          backgroundColor:
                            MAP_CATEGORIES.find(
                              (c) => c.key === marker.category
                            )?.color + "18",
                          border: `1px solid ${
                            MAP_CATEGORIES.find(
                              (c) => c.key === marker.category
                            )?.color
                          }40`,
                          padding: "2px 6px",
                          borderRadius: 3,
                          display: "inline-block",
                          marginBottom: 6,
                        }}
                      >
                        {
                          MAP_CATEGORIES.find(
                            (c) => c.key === marker.category
                          )?.icon
                        }{" "}
                        {
                          MAP_CATEGORIES.find(
                            (c) => c.key === marker.category
                          )?.label
                        }
                      </span>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "#222",
                          marginBottom: 4,
                        }}
                      >
                        {marker.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#666",
                          marginBottom: 4,
                        }}
                      >
                        {marker.address}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#444",
                          lineHeight: 1.6,
                          marginBottom: 4,
                        }}
                      >
                        {marker.description}
                      </div>
                      {marker.phone && (
                        <div style={{ fontSize: 11, color: "#4070e0" }}>
                          TEL: {marker.phone}
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>

        {/* 場所一覧（カード表示） */}
        <Box sx={{ mb: 6 }}>
          <Typography
            sx={{ fontSize: 16, fontWeight: 700, color: "#222", mb: 2 }}
          >
            一覧
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {filtered.map((marker) => {
              const cat =
                MAP_CATEGORIES.find((c) => c.key === marker.category) ||
                MAP_CATEGORIES[1];
              return (
                <Box
                  key={marker.id}
                  onClick={() => focusMarker(marker)}
                  sx={{
                    display: "flex",
                    overflow: "hidden",
                    border: `1px solid ${cat.color}25`,
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "box-shadow 0.15s ease",
                    maxWidth: "100%",
                    "&:hover": { boxShadow: `0 3px 12px ${cat.color}18` },
                  }}
                >
                  <Box
                    component="img"
                    src={getImage(marker)}
                    alt={marker.name}
                    sx={{
                      width: { xs: 90, sm: 110 },
                      minHeight: 90,
                      objectFit: "cover",
                      display: "block",
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      p: 1.5,
                      flexGrow: 1,
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.3,
                        bgcolor: `${cat.color}12`,
                        color: cat.color,
                        fontSize: 10,
                        fontWeight: 700,
                        px: 0.8,
                        py: 0.2,
                        borderRadius: "3px",
                        mb: 0.5,
                      }}
                    >
                      {cat.icon} {cat.label}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#222",
                        mb: 0.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {marker.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#888",
                        mb: 0.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {marker.address}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, color: cat.color, fontWeight: 600 }}
                    >
                      地図で見る &gt;
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* フッターナビ */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#eae8e0",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {FOOTER_NAV.map((item) => (
            <Box
              key={item.label}
              onClick={() => navigate(item.path)}
              sx={{
                px: { xs: 1.5, sm: 3 },
                py: 1.5,
                cursor: "pointer",
                fontSize: { xs: 11, sm: 14 },
                color: "#333",
                textDecoration: item.underline ? "underline" : "none",
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "rgba(0,0,0,0.05)" },
              }}
            >
              {item.label}
            </Box>
          ))}
          <Box
            sx={{
              px: { xs: 1, sm: 2 },
              py: 1.5,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              "&:hover": { bgcolor: "rgba(0,0,0,0.05)" },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 20, color: "#666" }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
